<div class="product-sync">
  <p class="intro">
    Pulls {{ source() }} collections into this vendor's catalogue. New products are
    created; existing ones have their price, quantity, description and options updated.
    Images are uploaded for new products only — a re-sync leaves existing images alone.
  </p>

  @if (note()) {
    <p class="alert-warn">{{ note() }}</p>
  }

  @if (activeLock(); as lock) {
    <div class="alert-warn sync-lock-banner">
      <div class="sync-lock-row">
        <mat-icon class="inline-icon">sync</mat-icon>
        <span class="sync-lock-text">
          @if (jobFor(lock.collection); as job) {
            {{ lock.collection }} is syncing (see below) — every other button here waits
            until it finishes.
          } @else {
            {{ lock.collection }} is syncing, started {{ lock.startedAt | date: 'HH:mm' }}
            @if (!lock.isThisInstance) {
              on another admin-backend instance ({{ lock.host }})
            }
            — every button here waits until it finishes.
            @if (lock.cancelRequested) {
              A cancel was requested; it will stop within a few seconds.
            } @else if (!lock.total) {
              Starting…
            }
          }
        </span>
        @if (canCancelRemote(lock.collection)) {
          <button
            mat-stroked-button
            color="warn"
            class="cancel-btn sync-lock-cancel"
            (click)="cancel(lock.collection)"
          >
            Cancel
          </button>
        }
      </div>

      <!-- The local job below already shows its own progress bar and log; this one is
           only for a sync with no job here — started elsewhere, or this instance's own
           lock surviving a page refresh that lost the in-memory job. -->
      @if (!jobFor(lock.collection) && lock.total > 0) {
        <mat-progress-bar mode="determinate" [value]="remoteProgress(lock)"></mat-progress-bar>
        <p class="progress-line">
          {{ lock.done }} / {{ lock.total }} — {{ lock.currentTitle || 'starting…' }}
        </p>
      }
    </div>
  }

  @if (error()) {
    <div class="alert-error">{{ error() }}</div>
  }

  @if (loading()) {
    <p class="muted">Loading collections…</p>
  }

  @for (c of collections(); track c.collection) {
    <div class="sync-card">
      <div class="sync-head">
        <div class="sync-mapping">
          <a [href]="c.sourceUrl" target="_blank" rel="noopener" class="source-link">
            {{ c.sourceUrl }}
            <mat-icon class="ext-icon">open_in_new</mat-icon>
          </a>

          <!-- The whole point of this page is that it is obvious which link writes
               to which category, so the mapping is spelled out rather than implied. -->
          @if (!c.routesByName) {
            <div class="mapping-line">
              <mat-icon class="arrow">south_east</mat-icon>
              <span class="path">
                @if (c.subsubcategoryId !== null) {
                  {{ c.subcategoryName || '—' }}
                  <span class="sep">›</span>
                  <strong>{{ c.categoryName || c.expectedName }}</strong>
                  <span class="id">(subsubcategory {{ c.subsubcategoryId }})</span>
                } @else {
                  <!-- No leaf, so naming the subcategory twice would just be noise. -->
                  <strong>{{ c.subcategoryName || c.expectedName }}</strong>
                  <span class="id">· filed directly here, with no type</span>
                }
              </span>
            </div>
          } @else {
            <!-- One collection, many destinations: there is no single category to
                 name, so say the rule and show where products actually are. -->
            <div class="mapping-line">
              <mat-icon class="arrow">call_split</mat-icon>
              <span class="path">
                @if (commonSubcategory(c); as sub) {
                  {{ sub }}
                  <span class="sep">›</span>
                }
                <strong>{{ c.label }}</strong>
                <span class="id">
                  · sorted by product name across {{ c.targets.length }} destinations
                </span>
              </span>
            </div>
            @if (usedTargets(c).length) {
              <div class="chips">
                @for (t of usedTargets(c); track t.categoryName) {
                  <span class="chip">{{ t.categoryName }} <b>{{ t.products }}</b></span>
                }
              </div>
            }
            @for (t of badTargets(c); track t.categoryName) {
              <div class="alert-warn">
                Destination {{ t.subsubcategoryId }}
                {{ t.categoryName ? '("' + t.categoryName + '")' : '' }}
                is {{ t.categoryStatus || 'missing' }} — the sync will refuse to run.
              </div>
            }
          }

          @if (c.sharedWith.length) {
            <p class="note">
              Shares this category with {{ c.sharedWith.join(', ') }}, so the product count
              above covers both.
            </p>
          }

          @for (m of c.nameMismatches; track m.id) {
            <div class="alert-warn">
              @if (m.actual === null) {
                Category {{ m.id }} ("{{ m.expected }}") no longer exists.
              } @else {
                Category {{ m.id }} is now named "{{ m.actual }}" but the importer expects
                "{{ m.expected }}".
              }
              The sync will refuse to run until they match.
            </div>
          }
          @if (c.categoryStatus && c.categoryStatus !== 'active') {
            <div class="alert-warn">Category status is "{{ c.categoryStatus }}", not active.</div>
          }
        </div>

        <div class="sync-stats">
          <div class="stat">
            <span class="stat-num">{{ c.products | number }}</span>
            <span class="stat-label">products</span>
          </div>
          <div class="stat">
            <span class="stat-num">{{ c.inStock | number }}</span>
            <span class="stat-label">in stock</span>
          </div>
          <div class="stat stat-wide">
            <span class="stat-num-sm">
              {{ c.lastUpdated ? (c.lastUpdated | date: 'd MMM y, HH:mm') : 'never' }}
            </span>
            <span class="stat-label">last updated</span>
          </div>
        </div>

        <!-- Markup applies to every price this button writes. Shown as a
             percentage because that is how the rate is discussed, and stored as
             the multiplier the importers use. -->
        <div class="markup-row">
          <label class="markup-label" [for]="'markup-' + c.collection">Markup</label>
          <input
            [id]="'markup-' + c.collection"
            type="number"
            class="markup-input"
            min="0"
            max="400"
            step="1"
            [value]="markupPercent(c)"
            (input)="onMarkupInput(c.collection, $any($event.target).value)"
          />
          <span class="markup-suffix">%</span>
          <button
            mat-stroked-button
            class="markup-save"
            [disabled]="!markupDirty(c.collection) || savingMarkup() === c.collection"
            (click)="saveMarkup(c)"
          >
            {{ savingMarkup() === c.collection ? 'Saving…' : 'Save' }}
          </button>
          @if (c.markupOverridden) {
            <button mat-button class="markup-reset" (click)="resetMarkup(c)"
                    matTooltip="Back to the rate written in the importer">
              Reset
            </button>
          }
          <span class="markup-note muted">
            @if (c.markupOverridden) {
              set here · importer default {{ percentOf(c.defaultMarkup) }}%
            } @else {
              importer default
            }
          </span>
        </div>

        <div class="sync-actions">
          <button
            mat-flat-button
            color="primary"
            class="sync-btn"
            [disabled]="isActive(c.collection) || vendorLocked()"
            [matTooltip]="
              !isActive(c.collection) && vendorLocked()
                ? (activeLock()?.collection ?? '') + ' is syncing — wait for it to finish'
                : ''
            "
            (click)="sync(c.collection)"
          >
            <mat-icon>sync</mat-icon>
            {{ isActive(c.collection) ? 'Syncing…' : 'Sync now' }}
          </button>
          @if (isActive(c.collection)) {
            <button
              mat-stroked-button
              color="warn"
              class="cancel-btn"
              [disabled]="jobFor(c.collection)?.cancelRequested"
              (click)="cancel(c.collection)"
            >
              <mat-icon>stop_circle</mat-icon>
              {{ jobFor(c.collection)?.cancelRequested ? 'Cancelling…' : 'Cancel' }}
            </button>
          }
        </div>
      </div>

      @if (jobFor(c.collection); as job) {
        <div class="sync-job">
          @if (job.state === 'queued') {
            <p class="muted">
              Queued — waiting for the running sync to finish. Runs are serialised because
              {{ source() }} can throttle us if several run at once.
            </p>
          }

          @if (job.state === 'running') {
            <mat-progress-bar
              [mode]="job.total ? 'determinate' : 'indeterminate'"
              [value]="progress(c.collection)"
            ></mat-progress-bar>
            <p class="progress-line">
              {{ job.done }} / {{ job.total }} — {{ job.currentTitle || 'starting…' }}
            </p>
          }

          @if (job.state === 'done' && job.result) {
            <p class="result-ok">
              <mat-icon class="inline-icon">check_circle</mat-icon>
              {{ job.result.inserted }} added, {{ job.result.updated }} updated,
              {{ job.result.options }} options
              @if (job.result.failures.length) {
                <span class="failed-count">· {{ job.result.failures.length }} failed</span>
              }
              <span class="muted">
                · finished {{ job.finishedAt | date: 'd MMM y, HH:mm' }}
              </span>
            </p>
          }

          @if (job.state === 'cancelled') {
            <p class="result-bad">
              <mat-icon class="inline-icon">stop_circle</mat-icon>
              @if (job.total) {
                Cancelled after {{ job.done }} of {{ job.total }} products —
                {{ job.result?.inserted ?? 0 }} added and {{ job.result?.updated ?? 0 }} updated
                before the stop are kept. Nothing was set inactive.
              } @else {
                Cancelled before it started. Nothing was changed.
              }
              <span class="muted">· {{ job.finishedAt | date: 'd MMM y, HH:mm' }}</span>
            </p>
          }

          @if (job.state === 'failed') {
            <p class="result-bad">
              <mat-icon class="inline-icon">error</mat-icon>
              Sync failed: {{ job.error }}
            </p>
          }

          <!-- Anything the operator should act on is surfaced here rather than being
               left for someone to spot in the log. -->
          @if (job.result) {
            @if (job.result.routed.length > 1) {
              <div class="chips chips-result">
                @for (r of job.result.routed; track r.name) {
                  <span class="chip chip-result">{{ r.name }} <b>{{ r.count }}</b></span>
                }
              </div>
            }
            @for (n of job.result.mergeNotes; track n) {
              <p class="note">Merged duplicate listing — {{ n }}</p>
            }
            @for (t of job.result.skippedNotLivestock; track t) {
              <p class="note">Skipped, not livestock — {{ t }}</p>
            }
            @for (r of job.result.keptInPlace; track r.id) {
              <p class="note">
                #{{ r.id }} "{{ r.name }}" is listed in more than one collection — left in
                category {{ r.stayedIn ?? 'none' }} rather than moved to
                {{ r.wouldHaveMovedTo ?? 'none' }}.
              </p>
            }
            @if (job.result.moved?.length) {
              <p class="note">
                Moved {{ job.result.moved!.length }} product(s) into the category their name
                routes to:
                @for (m of job.result.moved!; track m.id) {
                  #{{ m.id }} "{{ m.name }}"{{ $last ? "" : "," }}
                }
              </p>
            }
            @if (job.result.deactivated?.length) {
              <p class="alert-warn">
                Set {{ job.result.deactivated!.length }} product(s) to inactive — no longer listed on
                {{ source() }} for this category:
                @for (d of job.result.deactivated!; track d.id) {
                  #{{ d.id }} "{{ d.name }}"{{ $last ? "" : "," }}
                }
              </p>
            }
            @if (job.result.markedSoldOut?.length) {
              <p class="alert-warn">
                Marked {{ job.result.markedSoldOut!.length }} product(s) sold out (stock 0) — no longer
                anywhere on {{ source() }}:
                @for (d of job.result.markedSoldOut!; track d.id) {
                  #{{ d.id }} "{{ d.name }}"{{ $last ? "" : "," }}
                }
              </p>
            }
            @for (o of job.result.orphans; track o.id) {
              <p class="alert-warn">
                #{{ o.id }} "{{ o.name }}" is in this category but no longer listed on
                {{ source() }} — most likely renamed upstream, which leaves a duplicate of
                the same fish. Check it and remove the stale one if so.
              </p>
            }
            @for (t of job.result.suspectTitles; track t) {
              <p class="alert-warn">
                "{{ t }}" may state a quantity that was read as one fish — check its price.
              </p>
            }
            @for (f of job.result.failures; track f.title) {
              <p class="alert-error">{{ f.title }}: {{ f.error }}</p>
            }
          }

          @if (job.log.length) {
            <button mat-button class="log-toggle" (click)="toggleLog(c.collection)">
              {{ openLog() === c.collection ? 'Hide log' : 'Show log' }}
            </button>
            @if (openLog() === c.collection) {
              <pre class="log">{{ job.log.join('\n') }}</pre>
            }
          }
        </div>
      }
    </div>
  }
</div>
